/**
 * What sharing an item with mods registers: the list of things a mod may do
 * with it, each one a named action on OPBridge.
 *
 * Operator, 2026-09-29: *"anything including run a program should be
 * shareable with mods"*, then *"literally everything"*.
 *
 * ─── One implementation, not two ───
 *
 * Every operation is RECORDED from the real send code, never re-derived:
 * `opbridge/store.ts::captureRequests` runs the same dispatch function a
 * finger press runs, against a stand-in bridge that records instead of
 * sending, and the recording becomes the payload. So a shared transpose, a
 * shared light colour or a shared OBS scene is byte-for-byte what the item
 * itself sends, and a later change to the send code changes both. What this
 * file mirrors by hand is only the COMPONENT layer - which steps a tap builds
 * - and `mod_share_parity` checks that against real taps.
 *
 * ─── Shapes ───
 *
 * A named action is one press with no arguments (OPBridge DEC-517). So:
 *
 *   PRESS         one operation (a PC, a scene, a program, a tap).
 *   ON / OFF      where the item decides the direction from LOCAL state at
 *                 press time (a device toggle, a metronome toggle, a mixer
 *                 mute), a mod gets two explicit operations - "on" and
 *                 "off" - rather than a guess made on a different machine.
 *   CHOICE        one operation per option (grid cells, transpose values,
 *                 OPVisual cells).
 *   VALUE         faders, XY, levels: one operation with `inputs`, the slot
 *                 a mod's number lands in. Found by capturing twice with
 *                 different values and diffing, and refused unless the value
 *                 lands VERBATIM - OPBridge will write the mod's number there
 *                 as-is, so a send that transforms it cannot be templated.
 *
 * ─── What OPBridge must support ───
 *
 * `midi`, `osc` and `dispatch` payloads work today. Three are PROPOSED
 * (asked of OPBridge 2026-09-29) and used only on a bridge advertising them
 * in `/health.capabilities` (see `needsOf`):
 *
 *   route     replay one of OPBridge's own routes (OBS, programs, lights,
 *             timelines, engines, Stream Deck) - `named_action_route`.
 *   sequence  several sends in order (a tap = on then off, a preset change,
 *             an Ableton chain) - `named_action_sequence`.
 *   inputs    a value slot - `named_action_inputs`.
 */

import type { LayoutItem, ButtonItem, ButtonGridItem } from '../layout/projectStore.ts';
import { activePedals } from '../layout/projectStore.ts';
import type { BridgeConfig } from '../opbridge/storage.ts';
import { bridges, captureRequests, resolveBridges, type CapturedRequest } from '../opbridge/store.ts';
import type { NamedActionInput } from '../opbridge/httpClient.ts';
import {
  fireAction, armLfo, stopLfoAction, pushSequencer, armSequencerAction, pushMacro, armMacroAction,
  pushToggle, sendTranspose, sendSceneCopy, sendSceneClearAll, sendOpVisual, sendAutotuneCc,
  sendMixerVolume, sendMixerMute, sendMixerSolo, sendMixerArm, sendMixerPan, sendMixerSend, sendMixerRescan,
  sendDmxState, sendDmxEffect, sendDmxBlackout, executeStreamDeckAction, sendOpQuantizeAmount, OP_QUANTIZE_DEFAULT_AMOUNT,
} from '../opbridge/dispatch.ts';
import { buttonWire, buttonToggleConfig } from '../midiTrigger/ruleSync.ts';
import { pedalBridgeRowId } from '../pedals/bridgeBinding.ts';
import { effectiveNth } from '../ableton/deviceControl.ts';
import { sequencers } from '../sequencer/store.ts';
import { macros } from '../macros/store.ts';
import { getTimeline } from '../timeline/store.ts';
import { pushTimeline, startTimelineAction, stopTimelineAction, jumpToSectionAction, fireTimelineAction, launcherTargets, launcherDefinition, bridgeJumpStarts, bridgeCarriesPresses, timelineParts } from '../timeline/bridgeSync.ts';
import { scheduleTimelineSync } from '../timeline/autoSync.ts';
import { isChain } from '../timeline/chain.ts';
import { transposeForCapo } from '../transpose/session.ts';
import {
  AUTOTUNE_KEY_CC, AUTOTUNE_SCALE_CC, AUTOTUNE_STRENGTH_CC, KEY_CC_VALUES, SCALE_CC_VALUES,
  NOTE_NAMES, DEFAULT_AUTOTUNE_CHANNEL, type AutotuneScale,
} from '../autotune/session.ts';
import { getBank, bankUnion, homeLook } from '../rigState/store.ts';
import { parseMidiTargetId } from '../liveState/resolve.ts';
import { DEFAULT_PORT_TOKEN } from '../liveState/sendCache.ts';
import { resolveTarget, targetSubfixture } from '../lighting/targets.ts';
import { stageColorByLabel, rgb255 } from '../lighting/palette.ts';
import { presetFireBody } from '../lighting/presets.ts';
import {
  actionMsgs, actionFace, stopAllMsg, blackoutMsg, operatorScreenMsg,
  layerClearMsg, layerEnabledMsg, layerBlendMsg, layerSpeedMsg, layerOpacityMsg, masterOpacityMsg,
  lookMsg, camShakeMsg, vizMacroMsg, vizVariationMsg, OPVISUAL_LAYERS, OPV_BLENDS, OPV_SPEEDS, OPV_LOOK_PARAMS, type OpvMsg,
} from '../opvisual/wire.ts';
import { deriveCenterAmplitude } from '../components/SineLfo.tsx';
import { SCALES as WAVES_SCALES } from '../components/WavesAutotune.tsx';
import { snapshotCcValue } from '../components/SnapshotChange.tsx';
import { ulid } from '../utils/ulid.ts';
import { cellFace, cellKey } from '../ableton/rows.ts';
import { hueSceneAction } from '../lighting/hueScenes.ts';

export const CONSUMER = 'opbuttons';

// ─── Payload shapes ─────────────────────────────────────────────────────

/** One step OPBridge can replay. `route` is PROPOSED. */
export type StepPayload =
  | { kind: 'midi'; port: string; bytes: number[] }
  | { kind: 'osc'; address: string; args: unknown[]; target: string }
  | { kind: 'dispatch'; module: string; command: string; args: Record<string, unknown>; consumer: string }
  | { kind: 'route'; method: 'POST' | 'PUT' | 'DELETE'; path: string; body: unknown };

/** What a share registers. `sequence` is PROPOSED. */
export type SharePayload = StepPayload | { kind: 'sequence'; steps: StepPayload[] };

export type Need = 'route' | 'sequence' | 'inputs';

/** The `/health.capabilities` flag each proposed shape is gated on. */
export const NEED_CAPABILITY: Record<Need, string> = {
  route: 'named_action_route',
  sequence: 'named_action_sequence',
  inputs: 'named_action_inputs',
};

export interface ShareOp {
  /** '' = the item's only operation (action id = item id, so existing
   *  button shares keep their ids); otherwise suffixed onto the item id. */
  key: string;
  /** What a mod reads in OPMod: "<item> - <operation>". */
  label: string;
  payload: SharePayload;
  inputs?: NamedActionInput[];
  /** Requests replayed on OPBridge BEFORE registering, so a mod's first
   *  press works even if the item was never tapped on this rig: a toggle
   *  definition, a sequencer/macro pattern, a timeline. */
  setup?: CapturedRequest[];
}

export type SharePlan =
  | { ops: ShareOp[]; notes: string[] }
  | { unsupported: string };

/**
 * The action id OPBridge stores an operation under: `<itemId>.<key>`, or the
 * bare item id for an item's only operation (so shares made before
 * 2026-09-29 keep their ids).
 *
 * The key is ENCODED into `[A-Za-z0-9-]` plus `_xx` escapes, because it rides
 * a URL path segment (`/actions/{id}/fire`) and OPBridge scopes match one
 * segment per `*`: a rec-quant key like `q1/16` would otherwise split the
 * path, and an OPVisual speed like `0.25` would collide with the `.`
 * separator. `_` itself is escaped, so the encoding is reversible and two
 * keys can never map to one id.
 */
export function actionIdFor(itemId: string, key: string): string {
  if (!key) return itemId;
  const safe = key.replace(/[^A-Za-z0-9-]/g, (c) => `_${c.charCodeAt(0).toString(16).padStart(2, '0')}`);
  return `${itemId}.${safe}`;
}

/** Which proposed OPBridge shapes an operation relies on. */
export function needsOf(op: ShareOp): Need[] {
  const needs = new Set<Need>();
  const walk = (p: SharePayload): void => {
    if (p.kind === 'sequence') { needs.add('sequence'); p.steps.forEach(walk); return; }
    if (p.kind === 'route') needs.add('route');
  };
  walk(op.payload);
  if (op.inputs && op.inputs.length > 0) needs.add('inputs');
  return [...needs];
}

// ─── Recording ──────────────────────────────────────────────────────────

/** A recorded request as a replayable step. GETs are reads a send does on
 *  the way (none today) and are not part of what a press does. */
function toStep(r: CapturedRequest): StepPayload | null {
  if (r.method === 'GET') return null;
  const b = (r.body ?? {}) as Record<string, unknown>;
  if (r.method === 'POST' && r.path === '/midi/send' && Array.isArray(b['bytes']) && typeof b['port'] === 'string') {
    return { kind: 'midi', port: b['port'], bytes: b['bytes'] as number[] };
  }
  if (r.method === 'POST' && r.path === '/osc/send' && typeof b['address'] === 'string') {
    return {
      kind: 'osc', address: b['address'], args: (b['args'] as unknown[]) ?? [],
      target: typeof b['target'] === 'string' ? b['target'] : '127.0.0.1:7000',
    };
  }
  return { kind: 'route', method: r.method, path: r.path, body: r.body ?? {} };
}

/** Record `send` against `cfg` and return it as ONE payload, or null when
 *  the send code refused to send anything (an unconfigured item). */
async function record(cfg: BridgeConfig, send: (t: string) => Promise<unknown>): Promise<SharePayload | null> {
  const steps = (await captureRequests(cfg, send)).map(toStep).filter((s): s is StepPayload => s !== null);
  if (steps.length === 0) return null;
  return steps.length === 1 ? steps[0]! : { kind: 'sequence', steps };
}

/** Record only the setup requests (pushes), replayed as-is at share time. */
async function recordSetup(cfg: BridgeConfig, send: (t: string) => Promise<unknown>): Promise<CapturedRequest[]> {
  return (await captureRequests(cfg, send)).filter((r) => r.method !== 'GET');
}

// ─── Value slots ────────────────────────────────────────────────────────

type Json = unknown;

/** JSON pointers where `a` and `b` differ, both being numbers. */
function numberDiffs(a: Json, b: Json, path = ''): Array<{ at: string; a: number; b: number }> {
  if (typeof a === 'number' && typeof b === 'number') return a === b ? [] : [{ at: path, a, b }];
  if (Array.isArray(a) && Array.isArray(b) && a.length === b.length) {
    return a.flatMap((x, i) => numberDiffs(x, b[i], `${path}/${i}`));
  }
  if (a && b && typeof a === 'object' && typeof b === 'object' && !Array.isArray(a) && !Array.isArray(b)) {
    const ka = Object.keys(a as object).sort();
    const kb = Object.keys(b as object).sort();
    if (ka.join('\u0000') !== kb.join('\u0000')) return [{ at: path, a: NaN, b: NaN }];
    return ka.flatMap((k) => numberDiffs((a as Record<string, Json>)[k], (b as Record<string, Json>)[k], `${path}/${k.replace(/~/g, '~0').replace(/\//g, '~1')}`));
  }
  return JSON.stringify(a) === JSON.stringify(b) ? [] : [{ at: path, a: NaN, b: NaN }];
}

function setAt(obj: Json, pointer: string, v: number): void {
  const parts = pointer.split('/').slice(1).map((p) => p.replace(/~1/g, '/').replace(/~0/g, '~'));
  let cur = obj as Record<string, unknown>;
  for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]!] as Record<string, unknown>;
  cur[parts[parts.length - 1]!] = v;
}

interface Range { min: number; max: number; integer?: boolean }

/** Two distinct in-range probe values that are unlikely to collide with
 *  anything else in a payload. */
function probes(r: Range, salt: number): [number, number] {
  if (r.integer) {
    const span = r.max - r.min;
    return [r.min + Math.max(1, Math.floor(span * 0.37)) + salt, r.min + Math.max(2, Math.floor(span * 0.61)) + salt];
  }
  const span = r.max - r.min;
  return [r.min + span * (0.3719 + salt * 0.01), r.min + span * (0.6143 + salt * 0.01)];
}

/**
 * A VALUE operation. `run(values, target)` must send exactly what the item
 * sends for those values. Refused (null) unless every input lands verbatim,
 * because OPBridge will write the mod's number into those slots as-is.
 */
async function valueOp(
  cfg: BridgeConfig,
  key: string,
  label: string,
  specs: Array<{ name: string; range: Range; defaultValue: number }>,
  run: (values: Record<string, number>, target: string) => Promise<unknown>,
): Promise<ShareOp | { error: string } | null> {
  const base: Record<string, number> = {};
  const alt: Record<string, number> = {};
  specs.forEach((s, i) => { const [p, q] = probes(s.range, i); base[s.name] = p; alt[s.name] = q; });
  const basePayload = await record(cfg, (t) => run(base, t));
  if (!basePayload) return null;
  const inputs: NamedActionInput[] = [];
  for (const s of specs) {
    const changed = await record(cfg, (t) => run({ ...base, [s.name]: alt[s.name]! }, t));
    const diffs = numberDiffs(basePayload, changed);
    const verbatim = diffs.length > 0 && diffs.every((d) => d.a === base[s.name] && d.b === alt[s.name]);
    if (!verbatim) return { error: `${label}: the value is transformed on the way out, so a mod's number cannot be dropped in as-is` };
    inputs.push({
      name: s.name, at: diffs.map((d) => d.at),
      min: s.range.min, max: s.range.max, ...(s.range.integer ? { integer: true } : {}),
    });
  }
  // The registered payload carries each input's real default in its slots.
  const payload = JSON.parse(JSON.stringify(basePayload)) as SharePayload;
  for (const [i, s] of specs.entries()) {
    const v = Math.min(s.range.max, Math.max(s.range.min, s.defaultValue));
    for (const at of inputs[i]!.at) setAt(payload, at, v);
  }
  return { key, label, payload, inputs };
}

// ─── Where a share registers ────────────────────────────────────────────

/** The bridges an item's own presses land on - a mod's press lands in the
 *  same place. Pedal-bound buttons and pedal kinds follow the PEDAL's bridge
 *  (a finger tap does; sharing used `bridgeIds` and registered elsewhere). */
export function shareTargets(item: LayoutItem): BridgeConfig[] {
  const pedalId = item.kind === 'button' || item.kind === 'presetChange' || item.kind === 'snapshotChange'
    ? item.pedalId : undefined;
  let ids: string[];
  if (pedalId) {
    const pd = activePedals().find((p) => p.id === pedalId);
    ids = [pedalBridgeRowId(pd) ?? ''];
  } else if (item.kind === 'blackout') {
    ids = ['all'];
  } else if (item.kind === 'timelineLauncher') {
    // Where an on-screen launch plays (launcherTargets): "Sends to", else the timeline's home. Registering
    // on the default bridge put a lighting launcher on a computer that owns no lights (2026-10-09).
    const tl = getTimeline(item.timelineId);
    ids = tl ? launcherTargets(tl, item.bridgeIds) : (item.bridgeIds ?? ['']);
  } else {
    ids = ('bridgeIds' in item ? item.bridgeIds : undefined) ?? [''];
  }
  const seen = new Map<string, BridgeConfig>();
  for (const t of ids) for (const b of resolveBridges(t, { ignorePanicLatch: true })) seen.set(b.id, b);
  return [...seen.values()];
}

// ─── Labels ─────────────────────────────────────────────────────────────

const KIND_NAMES: Partial<Record<LayoutItem['kind'], string>> = {
  button: 'Button', buttongrid: 'Grid', lfo: 'LFO', sequencer: 'Sequencer', macro: 'Macro',
  streamdeck: 'Stream Deck', fader: 'Fader', radial: 'Knob', xypad: 'XY pad', piano: 'Piano',
  transpose: 'Transpose', capo: 'Capo', recQuant: 'Rec quant', sceneCopy: 'Scene copy',
  wavesAutotune: 'Autotune', mixer: 'Mixer', presetChange: 'Preset', snapshotChange: 'Snapshot',
  rigStateButton: 'Rig look', lightColor: 'Light colour', lightLevel: 'Light level', lightFx: 'Light effect',
  blackout: 'Blackout', opvisualTrigger: 'OPVisual', opvisualGrid: 'OPVisual grid', timelineLauncher: 'Timeline',
};

export function itemName(item: LayoutItem): string {
  const n = (item as { name?: string }).name?.trim();
  return n || KIND_NAMES[item.kind] || item.kind;
}

// ─── Static refusals ────────────────────────────────────────────────────

/** Why an item cannot be shared at all, or null. Pure and synchronous, so
 *  the Inspector can disable the switch with the same answer sync uses. */
export function unshareableReason(item: LayoutItem): string | null {
  switch (item.kind) {
    case 'statusLed': case 'label': case 'display': case 'image':
      return 'This only shows things - it sends nothing, so there is nothing for a mod to press.';
    default: break;
  }
  if ('bridgeIds' in item && item.bridgeIds && item.bridgeIds.length === 0) {
    return 'This sends nowhere, so there is nothing to share. Pick where it sends first.';
  }
  return null;
}

// ─── The plan, per kind ─────────────────────────────────────────────────

type Built = ShareOp | { error: string } | null;

async function press(cfg: BridgeConfig, key: string, label: string, send: (t: string) => Promise<unknown>, setup?: CapturedRequest[]): Promise<Built> {
  const payload = await record(cfg, send);
  if (!payload) return null;
  return { key, label, payload, ...(setup && setup.length ? { setup } : {}) };
}

/** Every operation an item offers a mod on `cfg`. */
export async function planShare(item: LayoutItem, cfg: BridgeConfig): Promise<SharePlan> {
  const why = unshareableReason(item);
  if (why) return { unsupported: why };
  const name = itemName(item);
  const L = (op: string) => (op ? `${name} - ${op}` : name);
  const built: Built[] = [];
  const notes: string[] = [];

  switch (item.kind) {
    case 'button': {
      const r = await planButton(item, cfg, L, notes);
      if ('unsupported' in r) return r;
      built.push(...r);
      break;
    }
    case 'buttongrid': built.push(...await planGrid(item, cfg, L)); break;
    case 'lfo': {
      const { center, amplitude } = deriveCenterAmplitude(item.min, item.max);
      built.push(await press(cfg, 'start', L('start'), (t) => armLfo({
        lfoId: item.id, ccNumber: item.ccNumber, channel: item.channel, periodBeats: item.bars * 4,
        center, amplitude, waveform: item.waveform, loop: true,
      }, t)));
      built.push(await press(cfg, 'stop', L('stop'), (t) => stopLfoAction(item.id, t)));
      break;
    }
    case 'sequencer': {
      const s = sequencers().find((x) => x.id === item.sequencerId);
      if (!s) return { unsupported: 'This sequencer has no pattern yet.' };
      const setup = await recordSetup(cfg, (t) => pushSequencer(s, t));
      built.push(await press(cfg, '', L('start / stop'), (t) => armSequencerAction(s.id, t), setup));
      break;
    }
    case 'macro': {
      const m = macros().find((x) => x.id === item.macroId);
      if (!m) return { unsupported: 'This macro has no content yet.' };
      const setup = await recordSetup(cfg, (t) => pushMacro(m, t));
      built.push(await press(cfg, '', L('start / stop'), (t) => armMacroAction(m.id, t), setup));
      break;
    }
    case 'streamdeck': {
      if (!item.actionId) return { unsupported: 'No Stream Deck action chosen yet.' };
      built.push(await press(cfg, '', L(''), (t) => executeStreamDeckAction(item.actionId!, t)));
      break;
    }
    case 'fader': case 'radial': {
      built.push(await valueOp(cfg, '', L('value'), [{ name: 'value', range: { min: 0, max: 127, integer: true }, defaultValue: item.value }],
        (v, t) => fireAction({ type: 'midi.cc', id: ulid(), target: t, channel: item.channel + 1, cc: item.ccNumber, value: v['value']! })));
      break;
    }
    case 'xypad': {
      built.push(await valueOp(cfg, '', L('position'), [
        { name: 'x', range: { min: 0, max: 127, integer: true }, defaultValue: item.xValue },
        { name: 'y', range: { min: 0, max: 127, integer: true }, defaultValue: item.yValue },
      ], async (v, t) => {
        await fireAction({ type: 'midi.cc', id: ulid(), target: t, channel: item.xChannel + 1, cc: item.xCcNumber, value: v['x']! });
        await fireAction({ type: 'midi.cc', id: ulid(), target: t, channel: item.yChannel + 1, cc: item.yCcNumber, value: v['y']! });
      }));
      break;
    }
    case 'piano': {
      for (let o = 0; o < item.octaves; o++) {
        for (let s = 0; s < 12; s++) {
          const note = (item.rootOctave + 2) * 12 + o * 12 + s;
          const nm = `${NOTE_NAMES[s]}${item.rootOctave + o}`;
          built.push(await press(cfg, `n${note}`, L(`tap ${nm}`), async (t) => {
            await fireAction({ type: 'midi.note', id: ulid(), target: t, channel: item.channel + 1, note, velocity: item.velocity, duration: 0 });
            await fireAction({ type: 'midi.note', id: ulid(), target: t, channel: item.channel + 1, note, velocity: 0, duration: 0 });
          }));
        }
      }
      notes.push('A mod taps a key (note on, then off) - they cannot hold one.');
      break;
    }
    case 'transpose': {
      for (let n = item.start; n <= item.end; n++) {
        built.push(await press(cfg, `t${n}`, L(n > 0 ? `+${n}` : String(n)), (t) => sendTranspose(n, t)));
      }
      break;
    }
    case 'capo': {
      for (let f = item.start; f <= item.end; f++) {
        const n = transposeForCapo(f);
        built.push(await press(cfg, `f${f}`, L(`fret ${f}`), (t) => sendTranspose(n, t)));
      }
      notes.push('Capo frets include the current drop tuning; changing it re-shares.');
      break;
    }
    case 'recQuant': {
      const opq = item.quantizer === 'op-quantize';
      for (const label of item.labels) {
        built.push(await press(cfg, `q${label}`, L(label), (t) => fireAction({
          id: ulid(), type: 'ableton.recquant', label, ...(opq ? { via: 'op-quantize' as const } : {}), target: t,
        })));
      }
      if (opq) {
        built.push(await valueOp(cfg, 'amount', L('amount'), [{ name: 'value', range: { min: 0, max: 100, integer: true }, defaultValue: item.amount ?? OP_QUANTIZE_DEFAULT_AMOUNT }],
          (v, t) => sendOpQuantizeAmount(v['value']!, t)));
      }
      break;
    }
    case 'abletonRow': {
      // One operation per cell, the same step a tap fires (DEC-291). A
      // practice-clock row runs only in this app, as a practice button does.
      if (item.cells.some((s) => s.type === 'practice.transport' || ('clock' in s && s.clock === 'practice'))) {
        return { unsupported: 'This uses the practice clock, which runs only in this app - a mod cannot reach it.' };
      }
      for (const cell of item.cells) {
        const face = cellFace(cell);
        // "120 BPM", "Play from top", "1 Bar": the cell's own face.
        built.push(await press(cfg, cellKey(cell), L(face.l2 ? `${face.l1} ${face.l2}` : face.l1),
          (t) => fireAction({ ...cell, id: ulid(), target: t })));
      }
      break;
    }
    case 'sceneCopy': {
      for (let i = 0; i < item.count; i++) {
        const scene = item.firstScene + i;
        for (const verb of ['copy', 'paste', 'clear'] as const) {
          built.push(await press(cfg, `${verb}${scene}`, L(`${verb} scene ${scene}`), (t) => sendSceneCopy(verb, scene, t)));
        }
      }
      if (item.showDeleteAll) {
        built.push(await press(cfg, 'deleteAll', L('DELETE ALL scenes'), async (t) => {
          await sendSceneClearAll('press', t);
          await sendSceneClearAll('cancel', t);
        }));
      }
      break;
    }
    case 'wavesAutotune': {
      const ch = item.channel ?? DEFAULT_AUTOTUNE_CHANNEL;
      for (let pc = 0; pc < 12; pc++) {
        built.push(await press(cfg, `k${pc}`, L(`key ${NOTE_NAMES[pc]}`), (t) => sendAutotuneCc(ch, AUTOTUNE_KEY_CC, KEY_CC_VALUES[pc] ?? 0, t)));
      }
      for (const s of WAVES_SCALES) {
        built.push(await press(cfg, `s${s.id}`, L(`scale ${s.label}`), (t) => sendAutotuneCc(ch, AUTOTUNE_SCALE_CC, SCALE_CC_VALUES[s.id as AutotuneScale], t)));
      }
      if (item.showStrength) {
        built.push(await valueOp(cfg, 'strength', L('strength'), [{ name: 'value', range: { min: 0, max: 127, integer: true }, defaultValue: 64 }],
          (v, t) => sendAutotuneCc(ch, AUTOTUNE_STRENGTH_CC, v['value']!, t)));
      }
      break;
    }
    case 'mixer': built.push(...await planMixer(item, cfg, L)); break;
    case 'presetChange': {
      const pd = activePedals().find((p) => p.id === item.pedalId);
      if (!pd?.port) return { unsupported: 'This preset has no pedal with a MIDI port.' };
      const port = pd.port;
      const channel = pd.channel + 1;
      built.push(await press(cfg, '', L(''), async (t) => {
        await fireAction({ target: t, port, type: 'midi.cc', id: ulid(), channel, cc: 32, value: item.setlist & 0x7f });
        await fireAction({ target: t, port, type: 'midi.pc', id: ulid(), channel, program: item.program & 0x7f });
        if (typeof item.snapshot === 'number') {
          await fireAction({ target: t, port, type: 'midi.cc', id: ulid(), channel, cc: 69, value: item.snapshot & 0x7f });
        }
      }));
      break;
    }
    case 'snapshotChange': {
      const pd = activePedals().find((p) => p.id === item.pedalId);
      if (!pd?.port) return { unsupported: 'This snapshot has no pedal with a MIDI port.' };
      const port = pd.port;
      built.push(await press(cfg, '', L(''), (t) => fireAction({
        target: t, port, type: 'midi.cc', id: ulid(), channel: pd.channel + 1, cc: 69, value: snapshotCcValue(item.snapshot),
      })));
      break;
    }
    case 'rigStateButton': {
      const r = await planRigState(item.bankId, item.lookId, cfg, L);
      if ('unsupported' in r) return r;
      built.push(r.op);
      notes.push("A mod's recall sends the look but does not move this app's own transpose/autotune readout.");
      break;
    }
    case 'lightColor': {
      const sw = stageColorByLabel(item.color_label);
      const ids = resolveTarget(item.target).map((f) => f.id);
      if (!sw || ids.length === 0) return { unsupported: 'This colour or its lights no longer exist.' };
      const sub = targetSubfixture(item.target);
      built.push(await press(cfg, '', L(''), (t) => sendDmxState(ids, {
        rgb: rgb255(sw.rgb), on: item.color_label !== 'Off',
        ...(item.level !== undefined ? { level: item.level } : {}),
        ...(sub !== undefined ? { subfixture: sub } : {}),
      }, t)));
      break;
    }
    case 'lightLevel': {
      const ids = resolveTarget(item.target).map((f) => f.id);
      if (ids.length === 0) return { unsupported: 'These lights no longer exist.' };
      const sub = targetSubfixture(item.target);
      // `on` follows the level on a finger (on: level > 0). Both probe values
      // are above 0, so the template carries on:true; a mod's 0 is level 0
      // with power on, which is dark all the same.
      built.push(await valueOp(cfg, '', L('level'), [{ name: 'value', range: { min: 0, max: 255, integer: true }, defaultValue: item.value }],
        (v, t) => sendDmxState(ids, { level: v['value']!, on: v['value']! > 0, ...(sub !== undefined ? { subfixture: sub } : {}) }, t)));
      break;
    }
    case 'lightFx': {
      const ids = resolveTarget(item.target).map((f) => f.id);
      if (ids.length === 0) return { unsupported: 'These lights no longer exist.' };
      const sub = targetSubfixture(item.target);
      const tint = item.color_label ? stageColorByLabel(item.color_label) : undefined;
      built.push(await press(cfg, '', L(''), (t) => sendDmxEffect(ids, {
        effect: item.effect,
        ...(item.brightness !== undefined ? { brightness: item.brightness } : {}),
        ...(tint ? { color: rgb255(tint.rgb) } : {}),
        ...(sub !== undefined ? { subfixture: sub } : {}),
      }, t)));
      break;
    }
    case 'blackout': {
      built.push(await press(cfg, '', L(''), (t) => sendDmxBlackout(t)));
      if (bridges().length > 1) notes.push("A mod's Blackout blacks out the lights on the computer it is pressed on; a finger blacks out every computer.");
      break;
    }
    case 'opvisualTrigger': {
      const msgs = actionMsgs(item.action, item.quant);
      built.push(await press(cfg, '', L(actionFace(item.action)), (t) => sendOpVisual(msgs, [t], { via: item.sendVia })));
      break;
    }
    case 'opvisualGrid': built.push(...await planOpVisualGrid(item, cfg, L)); break;
    case 'timelineLauncher': {
      const stored = getTimeline(item.timelineId);
      if (!stored) return { unsupported: 'This timeline no longer exists.' };
      // The launcher's Loop setting travels in the pushed definition, as on screen (launcherDefinition).
      const tl = launcherDefinition(stored, item.loop);
      // A song on a computer that carries presses (2026-10-28, OPBridge S340): this computer gets only ITS part, or
      // nothing when it holds none (the press is carried to the computers that do), and the other parts are sent to
      // theirs. The whole timeline here would make its untied notes sound from two computers.
      const carried = tl.tier === 'song' && !isChain(tl) && bridgeCarriesPresses(cfg);
      const mine = carried ? timelineParts(tl).parts.find((p) => p.cfg.id === cfg.id) : undefined;
      // The other parts through the auto-push (debounced, nothing sent when they are current): this runs on every share
      // pass, and a push of the launcher's own definition here would fight the auto-push's on every edit.
      if (carried) scheduleTimelineSync(stored);
      const setup = !carried
        ? await recordSetup(cfg, (t) => pushTimeline(tl, t))
        : mine ? await recordSetup(cfg, (t) => pushTimeline(mine.def, t, { followsHere: mine.followsHere, siblings: mine.siblings })) : [];
      if (tl.tier === 'song') {
        built.push(await press(cfg, 'play', L('play'), (t) => startTimelineAction(tl.id, t), setup));
        built.push(await press(cfg, 'stop', L('stop'), (t) => stopTimelineAction(t), setup));
        // A pedal fires with no app in the loop, so where the bridge takes it the jump is ONE call that starts the
        // timeline only when it is not already the active Song (OPBridge S339): start-then-jump took the running
        // Song out at the press. Older bridges keep start-then-jump.
        const oneCall = bridgeJumpStarts(cfg);
        for (const sec of tl.sections) {
          built.push(await press(cfg, `sec${sec.id}`, L(`jump to ${sec.name}`), oneCall
            ? (t) => jumpToSectionAction(sec.id, t, tl.id)
            : async (t) => {
              await startTimelineAction(tl.id, t);
              await jumpToSectionAction(sec.id, t);
            }, setup));
        }
      } else {
        built.push(await press(cfg, '', L('fire'), (t) => fireTimelineAction(tl.id, t), setup));
      }
      break;
    }
    default:
      return { unsupported: 'This kind of item is not shareable yet.' };
  }

  const ops: ShareOp[] = [];
  for (const b of built) {
    if (!b) continue;
    if ('error' in b) { notes.push(b.error); continue; }
    ops.push(b);
  }
  if (ops.length === 0) return { unsupported: notes[0] ?? 'Nothing to share yet - finish setting this up first.' };
  return { ops, notes };
}

// ─── Buttons ────────────────────────────────────────────────────────────

async function planButton(
  item: ButtonItem, cfg: BridgeConfig, L: (op: string) => string, notes: string[],
): Promise<Built[] | { unsupported: string }> {
  const action = item.actionType ?? 'cc';
  const pedal = item.pedalId ? activePedals().find((p) => p.id === item.pedalId) : undefined;
  const port = pedal?.port;

  if (action === 'obs') {
    if (!item.obsScene) return { unsupported: 'No OBS scene chosen yet.' };
    const scene = item.obsScene;
    return [await press(cfg, '', L(`scene ${scene}`), (t) => fireAction({ type: 'obs.scene', id: ulid(), target: t, scene }))];
  }
  if (action === 'runProgram') {
    if (!item.runProgramId) return { unsupported: 'No program chosen yet.' };
    const programId = item.runProgramId;
    notes.push('A program set to "Ask me first" still asks you on that computer when a mod presses it.');
    return [await press(cfg, '', L('run'), (t) => fireAction({ type: 'program.launch', id: ulid(), target: t, programId }))];
  }
  if (action === 'lightPreset') {
    const lp = item.lightPreset;
    if (!lp?.presetId) return { unsupported: 'No preset chosen yet.' };
    // The same body a finger's press sends (`presetFireBody`), on the same
    // layer id, so a mod's play replaces this button's own layer rather than
    // stacking a second copy of the show.
    const body = presetFireBody(item.id, lp);
    const play = (t: string) => fireAction({
      type: 'lighting.fire', id: ulid(), target: t, layerId: item.id, preset: body.preset,
      ...(body.knobs ? { knobs: body.knobs } : {}),
      ...(body.fixtures ? { fixtures: body.fixtures } : {}),
      ...(body.lengthBeats !== undefined ? { lengthBeats: body.lengthBeats } : {}),
    });
    const stop = (t: string) => fireAction({ type: 'lighting.stop', id: ulid(), target: t, layerId: item.id });
    // A one-shot is one press. Toggle and hold have a real OFF, and the
    // direction is decided from THIS app's state at press time - so a mod
    // gets explicit play and stop (DEC-222's ON/OFF rule), not a guess.
    if ((item.mode ?? 'toggle') === 'trigger') return [await press(cfg, '', L('play'), play)];
    return [await press(cfg, 'play', L('play'), play), await press(cfg, 'stop', L('stop'), stop)];
  }
  if (action === 'hueScene') {
    const hs = item.hueScene;
    if (!hs?.scene) return { unsupported: 'No Hue scene chosen yet.' };
    // The press IS one module action (2026-10-23, OPBridge S331): OPBridge carries it to the PC holding the Hue
    // bridge, so nothing needs recording first.
    return [{ key: '', label: L(''), payload: { kind: 'dispatch', ...hueSceneAction(hs), consumer: CONSUMER } }];
  }
  if (action === 'abletonDevice') {
    const d = item.abletonDevice;
    if (!d?.trackName || !d.deviceName) return { unsupported: 'No track or device chosen yet.' };
    const set = (on: boolean) => (t: string) => fireAction({
      type: 'abletonDevice.setOn', id: ulid(), target: t, trackName: d.trackName, deviceName: d.deviceName,
      ...(d.nth !== undefined ? { nth: effectiveNth(d) } : {}), on,
    });
    return [await press(cfg, 'on', L('on'), set(true)), await press(cfg, 'off', L('off'), set(false))];
  }
  if (action === 'ableton') {
    const steps = item.abletonSteps ?? [];
    if (steps.length === 0) return { unsupported: 'No steps yet.' };
    if (steps.some((s) => ('clock' in s && s.clock === 'practice') || s.type === 'practice.transport')) {
      return { unsupported: 'This uses the practice clock, which runs only in this app - a mod cannot reach it.' };
    }
    if (steps.some((s) => s.type === 'opmotion.capture')) {
      return { unsupported: 'A capture is stored by this app when OP-Motion answers - a mod cannot capture. Share a Landing preset instead.' };
    }
    const hasToggle = steps.some((s) => s.type === 'ableton.metronome' && s.mode === 'toggle');
    const run = (metro: 'on' | 'off' | null) => async (t: string) => {
      for (const s of steps) {
        const step = metro && s.type === 'ableton.metronome' && s.mode === 'toggle' ? { ...s, mode: metro } : s;
        await fireAction({ ...step, target: t });
      }
    };
    if (!hasToggle) return [await press(cfg, '', L(''), run(null))];
    // A toggle is decided from Live's readback at press time; a mod gets the
    // two directions instead of a guess made on another machine.
    return [await press(cfg, 'on', L('metronome on'), run('on')), await press(cfg, 'off', L('metronome off'), run('off'))];
  }

  const wire = buttonWire(item, cfg);
  if (!wire) return { unsupported: 'No MIDI output is set for this computer.' };
  if (action === 'pc') {
    return [await press(cfg, '', L(''), (t) => fireAction({
      type: 'midi.pc', id: ulid(), target: t, channel: wire.channel + 1, program: item.program ?? 0, ...(port ? { port } : {}),
    }))];
  }
  // Note / CC buttons in EVERY mode share as an ON/OFF TOGGLE on OPBridge -
  // the operator's decision (2026-09-23): "mods see a shared button as an
  // ON/OFF TOGGLE", on = press, off = release. A mod cannot hold a button,
  // so a momentary button becomes one they switch on and later off.
  // `[REAFFIRMED 2026-09-29: an attempt to share momentary/trigger as a
  // one-press TAP (on then off) was reverted - it overrode that decision
  // unasked, and it would have broken the operator's one live share (a
  // momentary note button) until OPBridge ships the sequence shape.]`
  const tcfg = buttonToggleConfig(item, wire.channel);
  if (!tcfg) return { unsupported: 'This button has no on/off message to share.' };
  if ((item.mode ?? 'toggle') !== 'toggle') notes.push('Mods switch this ON and later OFF - they cannot hold it down or tap it.');
  const setup = await recordSetup(cfg, (t) => pushToggle(item.id, item.name, tcfg, t));
  return [{
    key: '', label: L(''),
    payload: { kind: 'dispatch', module: 'toggle', command: 'flip', args: { toggleId: item.id }, consumer: CONSUMER },
    ...(setup.length ? { setup } : {}),
  }];
}

// ─── Grid ───────────────────────────────────────────────────────────────

async function planGrid(item: ButtonGridItem, cfg: BridgeConfig, L: (op: string) => string): Promise<Built[]> {
  const cells = item.cells.map((c, i) => ({
    c, i,
    cc: c.ccNumber ?? ((item.baseCC + i) & 0x7f),
    ch: c.channel ?? item.channel,
    on: c.onValue ?? item.onValue,
  }));
  const cc = (x: typeof cells[number], v: number) => (t: string) =>
    fireAction({ type: 'midi.cc', id: ulid(), target: t, channel: x.ch + 1, cc: x.cc, value: v });
  const radio = (item.selectionMode ?? 'radio').startsWith('radio');
  const out: Built[] = [];
  for (const x of cells) {
    const nm = x.c.label?.trim() || `cell ${x.i + 1}`;
    if (radio) {
      // One lit cell: selecting sends every OTHER cell's off, then this
      // cell's on - a superset of a finger's "previous off, this on",
      // because a mod's machine cannot see which cell is lit.
      out.push(await press(cfg, `c${x.i}`, L(nm), async (t) => {
        for (const y of cells) if (y !== x) await cc(y, item.offValue)(t);
        await cc(x, x.on)(t);
      }));
    } else {
      out.push(await press(cfg, `c${x.i}on`, L(`${nm} on`), cc(x, x.on)));
      out.push(await press(cfg, `c${x.i}off`, L(`${nm} off`), cc(x, item.offValue)));
    }
  }
  return out;
}

// ─── Mixer ──────────────────────────────────────────────────────────────

const RETURN_BASE = 2000;

async function planMixer(item: Extract<LayoutItem, { kind: 'mixer' }>, cfg: BridgeConfig, L: (op: string) => string): Promise<Built[]> {
  const out: Built[] = [];
  const strip = async (idx: number, nm: string, opts: { mute: boolean; solo: boolean; arm: boolean; pan: boolean; sends: number }) => {
    const k = idx < 0 ? 'm' : idx >= RETURN_BASE ? `r${idx - RETURN_BASE}` : `t${idx}`;
    out.push(await valueOp(cfg, `${k}vol`, L(`${nm} volume`), [{ name: 'value', range: { min: 0, max: 1 }, defaultValue: 0.85 }],
      (v, t) => sendMixerVolume(idx, v['value']!, t)));
    if (opts.pan) {
      out.push(await valueOp(cfg, `${k}pan`, L(`${nm} pan`), [{ name: 'value', range: { min: -1, max: 1 }, defaultValue: 0 }],
        (v, t) => sendMixerPan(idx, v['value']!, t)));
    }
    if (opts.mute) {
      out.push(await press(cfg, `${k}mute`, L(`${nm} mute`), (t) => sendMixerMute(idx, true, t)));
      out.push(await press(cfg, `${k}unmute`, L(`${nm} unmute`), (t) => sendMixerMute(idx, false, t)));
    }
    if (opts.solo) {
      out.push(await press(cfg, `${k}solo`, L(`${nm} solo on`), (t) => sendMixerSolo(idx, true, t)));
      out.push(await press(cfg, `${k}unsolo`, L(`${nm} solo off`), (t) => sendMixerSolo(idx, false, t)));
    }
    if (opts.arm) {
      out.push(await press(cfg, `${k}arm`, L(`${nm} arm on`), (t) => sendMixerArm(idx, true, t)));
      out.push(await press(cfg, `${k}disarm`, L(`${nm} arm off`), (t) => sendMixerArm(idx, false, t)));
    }
    for (let s = 0; s < opts.sends; s++) {
      out.push(await valueOp(cfg, `${k}send${s}`, L(`${nm} send ${String.fromCharCode(65 + s)}`),
        [{ name: 'value', range: { min: 0, max: 1 }, defaultValue: 0 }], (v, t) => sendMixerSend(idx, s, v['value']!, t)));
    }
  };
  const sends = item.showSends ? (item.sendCount ?? 2) : 0;
  for (let i = 0; i < item.trackCount; i++) {
    const idx = item.bankOffset + i;
    await strip(idx, `track ${idx + 1}`, { mute: item.showMute, solo: !!item.showSolo, arm: !!item.showArm, pan: !!item.showPan, sends });
  }
  if (item.master) await strip(-1, 'master', { mute: false, solo: false, arm: false, pan: !!item.showPan, sends: 0 });
  if (item.returns) {
    for (let r = 0; r < (item.returnCount ?? 2); r++) {
      await strip(RETURN_BASE + r, `return ${String.fromCharCode(65 + r)}`, { mute: item.showMute, solo: !!item.showSolo, arm: false, pan: !!item.showPan, sends: 0 });
    }
  }
  out.push(await press(cfg, 'rescan', L('rescan tracks'), (t) => sendMixerRescan(t)));
  return out;
}

// ─── OPVisual grid ──────────────────────────────────────────────────────

async function planOpVisualGrid(item: Extract<LayoutItem, { kind: 'opvisualGrid' }>, cfg: BridgeConfig, L: (op: string) => string): Promise<Built[]> {
  const out: Built[] = [];
  const via = { via: item.sendVia };
  const one = (key: string, label: string, m: OpvMsg | null) => m
    ? press(cfg, key, L(label), (t) => sendOpVisual([m], [t], via))
    : Promise.resolve(null);
  // NO cell or column copies (2026-10-23, OPVisual's ask "one catalogue of OPVisual actions, not two"): OPVisual
  // publishes its own `opvisual-col-N` and `opvisual-cell-lL-C`, so ours would sit beside them as near-duplicates in
  // every binding list. A controller binds to OPVisual's (`controllers/assign.ts::opvOwnActions`), a mod picks
  // OPVisual's from OPBridge's list. The header, layers, knobs and look below are not published by OPVisual: kept.
  // The grid's own header (DEC-272): no chain and no PANIC (B/OUT covers it).
  out.push(await one('stopAll', 'stop all', stopAllMsg()));
  out.push(await one('blackout', 'blackout', blackoutMsg()));
  out.push(await one('operatorScreen', 'operator screen', operatorScreenMsg()));
  out.push(await one('shakeOn', 'cam shake on', camShakeMsg(true, 0.5)));
  out.push(await one('shakeOff', 'cam shake off', camShakeMsg(false)));
  if (item.showLayerControls) {
    for (let layer = 1; layer <= OPVISUAL_LAYERS; layer++) {
      out.push(await one(`l${layer}clear`, `layer ${layer} clear`, layerClearMsg(layer)));
      out.push(await one(`l${layer}on`, `layer ${layer} on`, layerEnabledMsg(layer, true)));
      out.push(await one(`l${layer}off`, `layer ${layer} off`, layerEnabledMsg(layer, false)));
      for (const b of OPV_BLENDS) out.push(await one(`l${layer}b${b}`, `layer ${layer} blend ${b}`, layerBlendMsg(layer, b)));
      for (const sp of OPV_SPEEDS) out.push(await one(`l${layer}s${sp}`, `layer ${layer} speed ${sp}x`, layerSpeedMsg(layer, sp)));
      out.push(await valueOp(cfg, `l${layer}opacity`, L(`layer ${layer} opacity`), [{ name: 'value', range: { min: 0, max: 1 }, defaultValue: 1 }],
        (v, t) => { const m = layerOpacityMsg(layer, v['value']!); return m ? sendOpVisual([m], [t], via) : Promise.resolve(); }));
    }
  }
  if (item.showViz !== false) {
    // The newest visualiser on air (layer 0), as the strip defaults to.
    out.push(await one('vizNewTake', 'visualiser new take', vizVariationMsg(0, item.quant)));
    for (let k = 1; k <= 6; k++) {
      out.push(await valueOp(cfg, `viz${k}`, L(`visualiser knob ${k}`), [{ name: 'value', range: { min: 0, max: 1 }, defaultValue: 0.5 }],
        (v, t) => { const m = vizMacroMsg(k, v['value']!, 0); return m ? sendOpVisual([m], [t], via) : Promise.resolve(); }));
    }
  }
  if (item.showMasterLook) {
    out.push(await valueOp(cfg, 'master', L('master'), [{ name: 'value', range: { min: 0, max: 1 }, defaultValue: 1 }],
      (v, t) => sendOpVisual([masterOpacityMsg(v['value']!)], [t], via)));
    for (const p of OPV_LOOK_PARAMS) {
      out.push(await valueOp(cfg, `look${p}`, L(`look ${p}`), [{ name: 'value', range: { min: 0, max: 1 }, defaultValue: 0.5 }],
        (v, t) => sendOpVisual([lookMsg(p, v['value']!)], [t], via)));
    }
  }
  return out;
}

// ─── Rig State ──────────────────────────────────────────────────────────

/**
 * Recall of one look, without touching this app's session. `recallLook`
 * goes through `writeTarget`, whose named targets (transpose, capo, autotune
 * key/scale) SET local session state as well as sending - recording that
 * would move the operator's live readouts just by sharing. So the writes are
 * resolved here the same way (look value, else HOME) and each is sent
 * through the dispatch-layer sender underneath those setters.
 */
async function planRigState(bankId: string | undefined, lookId: string | undefined, cfg: BridgeConfig, L: (op: string) => string):
  Promise<{ op: ShareOp } | { unsupported: string }> {
  const bank = bankId ? getBank(bankId) : undefined;
  const look = bank?.looks.find((l) => l.id === lookId);
  if (!bank || !look) return { unsupported: 'No look chosen yet.' };
  const home = homeLook(bank);
  const writes: Array<{ id: string; value: number | string }> = [];
  for (const id of bankUnion(bank)) {
    const e = look.values.find((v) => v.targetId === id) ?? home?.values.find((v) => v.targetId === id);
    if (e) writes.push({ id, value: e.value });
  }
  const payload = await record(cfg, async (t) => {
    for (const w of writes) {
      if (w.id === 'transpose' && typeof w.value === 'number') await sendTranspose(Math.max(-12, Math.min(12, Math.round(w.value))), t);
      else if (w.id === 'capo' && typeof w.value === 'number') await sendTranspose(Math.max(-12, Math.min(12, Math.round(transposeForCapo(w.value)))), t);
      else if (w.id === 'autotuneKey' && typeof w.value === 'number') {
        const pc = ((Math.round(w.value) % 12) + 12) % 12;
        await sendAutotuneCc(DEFAULT_AUTOTUNE_CHANNEL, AUTOTUNE_KEY_CC, KEY_CC_VALUES[pc] ?? 0, t);
      } else if (w.id === 'autotuneScale' && typeof w.value === 'string') {
        await sendAutotuneCc(DEFAULT_AUTOTUNE_CHANNEL, AUTOTUNE_SCALE_CC, SCALE_CC_VALUES[w.value as AutotuneScale], t);
      } else if (typeof w.value === 'number') {
        const p = parseMidiTargetId(w.id);
        if (!p) continue;
        const port = p.port === DEFAULT_PORT_TOKEN ? {} : { port: p.port };
        await (p.kind === 'cc'
          ? fireAction({ id: ulid(), target: t, type: 'midi.cc', ...port, channel: p.channel, cc: p.number, value: w.value })
          : fireAction({ id: ulid(), target: t, type: 'midi.note', ...port, channel: p.channel, note: p.number, velocity: w.value, duration: 0 }));
      }
    }
  });
  if (!payload) return { unsupported: 'This look has nothing to send.' };
  return { op: { key: '', label: L(look.name ?? 'recall'), payload } };
}
